import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import View from 'ol/View';
import Point from 'ol/geom/Point';
import { defaults as defaultInteractions } from 'ol/interaction/defaults';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';

@Component({
  selector: 'company-location-map',
  standalone: false,
  template: `
    <div #mapTarget class="map-target" role="region" aria-label="Company location map" tabindex="0"></div>
    <a class="map-attribution" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>
  `,
  styles: [`
    :host { position: relative; display: block; width: 100%; height: 200px; }
    .map-target { width: 100%; height: 100%; }
    .map-attribution {
      position: absolute; top: 0; right: 0; z-index: 1; padding: 2px 4px;
      color: #333; background: rgba(255, 255, 255, .9); font: 11px/1.4 Arial, sans-serif;
    }
  `],
})
export class CompanyLocationMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() latitude?: number;
  @Input() longitude?: number;
  @Input() zoom = 0;
  @Input() showMarker = false;
  @Input() markerRadius = 5;
  @Input() markerFill = 'red';
  @Input() markerStroke = 'black';
  @Input() markerStrokeWidth = 1;

  @ViewChild('mapTarget', { static: true }) private mapTarget!: ElementRef<HTMLDivElement>;

  private map?: Map;
  private readonly markerSource = new VectorSource<Feature<Point>>();
  private viewReady = false;

  constructor(private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.zone.runOutsideAngular(() => this.updateMap());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.viewReady) {
      this.zone.runOutsideAngular(() => this.updateMap(changes));
    }
  }

  ngOnDestroy(): void {
    this.viewReady = false;
    this.zone.runOutsideAngular(() => this.disposeMap());
    this.markerSource.dispose();
  }

  private updateMap(changes?: SimpleChanges): void {
    if (!Number.isFinite(this.longitude) || !Number.isFinite(this.latitude)) {
      this.disposeMap();
      return;
    }

    const center = fromLonLat([this.longitude!, this.latitude!]);
    const zoom = Number.isFinite(this.zoom) ? this.zoom : 0;

    if (!this.map) {
      // Map owns its ResizeObserver, so hidden/resized drawers are measured again automatically.
      this.map = new Map({
        target: this.mapTarget.nativeElement,
        controls: [],
        // The previous wrapper used defaults() without Map's onFocusOnly override.
        interactions: defaultInteractions(),
        layers: [
          new TileLayer({ source: new OSM() }),
          new VectorLayer({ source: this.markerSource }),
        ],
        view: new View({ center, zoom }),
      });
    } else {
      if (!changes || changes.latitude || changes.longitude) {
        this.map.getView().setCenter(center);
      }
      if (!changes || changes.latitude || changes.longitude || changes.zoom) {
        this.map.getView().setZoom(zoom);
      }
    }

    this.markerSource.clear();
    if (this.showMarker) {
      const marker = new Feature(new Point(center));
      marker.setStyle(new Style({
        image: new CircleStyle({
          radius: this.markerRadius,
          fill: new Fill({ color: this.markerFill }),
          stroke: new Stroke({ color: this.markerStroke, width: this.markerStrokeWidth }),
        }),
      }));
      this.markerSource.addFeature(marker);
    }
  }

  private disposeMap(): void {
    if (this.map) {
      const layers = this.map.getLayers().getArray().slice();
      this.map.getLayers().clear();
      this.map.setTarget(undefined);
      this.map.dispose();
      layers.forEach(layer => layer.dispose());
      this.map = undefined;
    }
    this.markerSource.clear();
  }
}
